import type { Models, Usage } from "@mainion/protocol";

/**
 * What workers' LLM calls used, all added up, like "12,300 tokens in, 2,100 out · $0.04".
 * The cost is what the API said, or else worked out from the model's price in `models`.
 */
export function describeUsage(usages: Usage[], models: Models): string {
    const input = usages.reduce((sum, usage) => sum + usage.input, 0);
    const output = usages.reduce((sum, usage) => sum + usage.output, 0);
    const costs = usages.map((usage) => costOf(usage, models));
    const known = costs.filter((cost) => cost !== undefined);
    const tokens = `${input.toLocaleString("en-US")} tokens in, ${output.toLocaleString("en-US")} out`;

    if (known.length === 0) return `${tokens} · price unknown`;
    const cost = dollars(known.reduce((sum, c) => sum + c, 0));

    return known.length < costs.length ? `${tokens} · ${cost} for the models with a known price` : `${tokens} · ${cost}`;
}

/** What one worker's LLM calls cost, in dollars. Undefined when neither the API nor the catalog says. */
function costOf(usage: Usage, models: Models): number | undefined {
    if (usage.cost !== undefined) return usage.cost;

    const price = Object.values(models)
        .flat()
        .find((model) => model.id === usage.model)?.price;

    return price && (usage.input * price.input + usage.output * price.output) / 1_000_000;
}

function dollars(amount: number): string {
    return amount > 0 && amount < 0.01 ? "less than $0.01" : `$${amount.toFixed(2)}`;
}
